<template>
  <div class="leadranker-dashboard">
    <v-container fluid class="px-6 py-4">
      <!-- Page Header -->
      <div class="page-header mb-6">
        <div class="header-content">
          <div class="page-title-section">
            <div class="d-flex align-center mb-2">
              <h1 class="page-title">
                <v-icon large class="vibrant-title-icon mr-3">mdi-trending-up</v-icon>
                LeadRanker AI Dashboard
                <span class="page-inline-subtitle">Intelligent lead scoring and sales pipeline optimization</span>
              </h1>
              <v-spacer></v-spacer>
              <div class="header-actions">
                <v-btn
                  color="primary"
                  @click="$router.push('/agents/leadranker-ai/leads')"
                  class="modern-btn mr-3"
                  elevation="2"
                >
                  <v-icon left>mdi-account-multiple</v-icon>
                  Manage Leads
                </v-btn>
                <v-btn 
                  color="info" 
                  @click="fetchDashboardData"
                  class="modern-btn"
                  elevation="2"
                >
                  <v-icon left>mdi-refresh</v-icon>
                  Refresh
                </v-btn>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Stats Cards -->
      <v-row class="mb-6">
        <v-col cols="12" sm="6" md="3" v-for="stat in stats" :key="stat.title">
          <v-card 
            class="stats-card"
            elevation="4"
            hover
          >
            <v-card-text class="stats-content">
              <div class="stats-icon-wrapper" :class="`${stat.color}--bg`">
                <v-icon size="28" color="white">{{ stat.icon }}</v-icon>
              </div>
              <div class="stats-text">
                <div class="stats-value">{{ stat.value }}</div>
                <div class="stats-label">{{ stat.title }}</div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Main Content Row -->
      <v-row>
        <!-- Hot Leads Section -->
        <v-col cols="12" md="8">
          <v-card class="hot-leads-card" elevation="6">
            <v-card-title class="modern-card-title">
              <div class="d-flex align-center">
                <v-icon class="mr-2" color="red">mdi-fire</v-icon>
                <h2 class="card-title">🔥 Hot Leads</h2>
                <v-spacer></v-spacer>
                <v-chip
                  small
                  :color="hotLeads.length > 0 ? 'red' : 'grey'"
                  dark
                >
                  {{ hotLeads.length }} Active
                </v-chip>
              </div>
            </v-card-title>
            <v-card-text>
              <div v-if="loading" class="text-center py-8">
                <v-progress-circular indeterminate color="primary"></v-progress-circular>
                <p class="mt-2">Loading hot leads...</p>
              </div>
              <div v-else-if="hotLeads.length === 0" class="text-center py-8">
                <v-icon size="64" color="grey lighten-2">mdi-account-search</v-icon>
                <h3 class="mt-2 grey--text">No Hot Leads Yet</h3>
                <p class="grey--text">Add some leads to see your hottest prospects here!</p>
              </div>
              <div v-else>
                <v-list class="hot-leads-list">
                  <v-list-item
                    v-for="lead in hotLeads"
                    :key="lead.id"
                    class="hot-lead-item"
                    @click="viewLeadDetails(lead.lead)"
                  >
                    <v-list-item-avatar>
                      <v-avatar :color="getTemperatureColor(lead.temperature)" size="40">
                        <v-icon dark>{{ getTemperatureIcon(lead.temperature) }}</v-icon>
                      </v-avatar>
                    </v-list-item-avatar>
                    <v-list-item-content>
                      <v-list-item-title class="lead-name">
                        {{ lead.lead.full_name }}
                      </v-list-item-title>
                      <v-list-item-subtitle class="lead-company">
                        {{ lead.lead.company_name }} • {{ lead.lead.designation }}
                      </v-list-item-subtitle>
                    </v-list-item-content>
                    <v-list-item-action>
                      <div class="lead-score-badge">
                        <v-chip
                          small
                          :color="getScoreColor(lead.overall_score)"
                          dark
                        >
                          {{ lead.overall_score }}/100
                        </v-chip>
                      </div>
                    </v-list-item-action>
                  </v-list-item>
                </v-list>
                <v-divider class="my-3"></v-divider>
                <div class="text-center">
                  <v-btn
                    text
                    color="primary"
                    @click="$router.push('/agents/leadranker-ai/scoring?temperature=hot')"
                  >
                    View All Hot Leads
                    <v-icon right>mdi-arrow-right</v-icon>
                  </v-btn>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Daily Summary Section -->
        <v-col cols="12" md="4">
          <v-card class="summary-card" elevation="6">
            <v-card-title class="modern-card-title">
              <div class="d-flex align-center">
                <v-icon class="mr-2" color="blue">mdi-chart-timeline-variant</v-icon>
                <h2 class="card-title">📊 Daily Summary</h2>
              </div>
            </v-card-title>
            <v-card-text>
              <div v-if="loading" class="text-center py-4">
                <v-progress-circular indeterminate color="primary" size="30"></v-progress-circular>
              </div>
              <div v-else>
                <!-- Today's Stats -->
                <div class="summary-section mb-4">
                  <h4 class="summary-heading">Today's Activity</h4>
                  <div class="summary-stats">
                    <div class="stat-row">
                      <span class="stat-label">Leads Scored:</span>
                      <span class="stat-value">{{ dailySummary.stats?.total_scored_today || 0 }}</span>
                    </div>
                    <div class="stat-row">
                      <span class="stat-label">Average Score:</span>
                      <span class="stat-value">{{ Math.round(dailySummary.stats?.avg_score_today || 0) }}/100</span>
                    </div>
                    <div class="stat-row">
                      <span class="stat-label">Hot Leads:</span>
                      <span class="stat-value">{{ dailySummary.stats?.hot_count || 0 }}</span>
                    </div>
                  </div>
                </div>

                <!-- Temperature Changes -->
                <div class="summary-section mb-4" v-if="dailySummary.temperature_upgrades?.length">
                  <h4 class="summary-heading">🌡️ Temperature Changes</h4>
                  <div class="temperature-changes">
                    <v-chip
                      v-for="change in dailySummary.temperature_upgrades.slice(0, 3)"
                      :key="change.id"
                      small
                      :color="getTemperatureColor(change.temperature)"
                      dark
                      class="ma-1"
                    >
                      <v-icon left small>{{ getTemperatureIcon(change.temperature) }}</v-icon>
                      {{ change.lead.first_name }} {{ change.lead.last_name }}
                    </v-chip>
                  </div>
                </div>

                <!-- Action Items -->
                <div class="summary-section">
                  <h4 class="summary-heading">🎯 Action Items</h4>
                  <v-list dense class="action-items">
                    <v-list-item v-if="hotLeads.length > 0">
                      <v-list-item-icon>
                        <v-icon color="red">mdi-fire</v-icon>
                      </v-list-item-icon>
                      <v-list-item-content>
                        <v-list-item-title>Follow up {{ hotLeads.length }} hot leads</v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                    <v-list-item v-if="dailySummary.new_warm_leads?.length">
                      <v-list-item-icon>
                        <v-icon color="orange">mdi-thermometer-plus</v-icon>
                      </v-list-item-icon>
                      <v-list-item-content>
                        <v-list-item-title>Nurture {{ dailySummary.new_warm_leads.length }} warm leads</v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                    <v-list-item>
                      <v-list-item-icon>
                        <v-icon color="blue">mdi-account-plus</v-icon>
                      </v-list-item-icon>
                      <v-list-item-content>
                        <v-list-item-title>Add new leads to pipeline</v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Recent Activity -->
      <v-row class="mt-6">
        <v-col cols="12">
          <v-card class="activity-card" elevation="6">
            <v-card-title class="modern-card-title">
              <div class="d-flex align-center">
                <v-icon class="mr-2" color="purple">mdi-history</v-icon>
                <h2 class="card-title">⏰ Recent Scoring Activity</h2>
                <v-spacer></v-spacer>
                <v-btn
                  text
                  color="primary"
                  @click="$router.push('/agents/leadranker-ai/scoring')"
                >
                  View All
                  <v-icon right>mdi-arrow-right</v-icon>
                </v-btn>
              </div>
            </v-card-title>
            <v-card-text>
              <div v-if="loading" class="text-center py-4">
                <v-progress-circular indeterminate color="primary" size="30"></v-progress-circular>
              </div>
              <div v-else-if="recentActivity.length === 0" class="text-center py-8">
                <v-icon size="64" color="grey lighten-2">mdi-clock-outline</v-icon>
                <h3 class="mt-2 grey--text">No Recent Activity</h3>
                <p class="grey--text">Lead scoring activity will appear here once you start adding leads.</p>
              </div>
              <v-simple-table v-else>
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th>Lead</th>
                      <th>Company</th>
                      <th>Score</th>
                      <th>Temperature</th>
                      <th>Recommendation</th>
                      <th>Scored At</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="activity in recentActivity.slice(0, 5)" :key="activity.id">
                      <td>
                        <strong>{{ activity.lead.full_name }}</strong>
                      </td>
                      <td>{{ activity.lead.company_name }}</td>
                      <td>
                        <v-chip
                          small
                          :color="getScoreColor(activity.overall_score)"
                          dark
                        >
                          {{ activity.overall_score }}/100
                        </v-chip>
                      </td>
                      <td>
                        <v-chip
                          small
                          :color="getTemperatureColor(activity.temperature)"
                          dark
                        >
                          <v-icon left small>{{ getTemperatureIcon(activity.temperature) }}</v-icon>
                          {{ activity.temperature.toUpperCase() }}
                        </v-chip>
                      </td>
                      <td>
                        <v-chip
                          small
                          :color="getRecommendationColor(activity.recommendation)"
                          dark
                        >
                          {{ activity.recommendation.toUpperCase() }}
                        </v-chip>
                      </td>
                      <td>{{ formatDate(activity.scored_at) }}</td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
import axios from '@/axios';

export default {
  name: "LeadRankerDashboard",
  data() {
    return {
      loading: false,
      statsData: {},
      hotLeads: [],
      dailySummary: {},
      recentActivity: [],
    };
  },
  computed: {
    stats() {
      return [
        {
          title: 'Total Leads',
          value: this.statsData.total_leads || 0,
          icon: 'mdi-account-multiple',
          color: 'blue'
        },
        {
          title: 'Hot Leads',
          value: this.statsData.hot_leads || 0,
          icon: 'mdi-fire',
          color: 'red'
        },
        {
          title: 'Warm Leads',
          value: this.statsData.warm_leads || 0,
          icon: 'mdi-thermometer-plus',
          color: 'orange'
        },
        {
          title: 'Avg Score',
          value: this.statsData.avg_score ? Math.round(this.statsData.avg_score) + '/100' : 'N/A',
          icon: 'mdi-chart-line',
          color: 'green'
        }
      ];
    }
  },
  async mounted() {
    await this.fetchDashboardData();
  },
  methods: {
    async fetchDashboardData() {
      this.loading = true;
      try {
        const [statsResponse, hotLeadsResponse, summaryResponse, activityResponse] = await Promise.all([
          axios.get('leads/stats'),
          axios.get('lead-scoring/hot-leads?limit=5'),
          axios.get('lead-scoring/daily-summary'),
          axios.get('lead-scoring?per_page=10')
        ]);

        this.statsData = statsResponse.data.data;
        this.hotLeads = hotLeadsResponse.data.data;
        this.dailySummary = summaryResponse.data.data;
        this.recentActivity = activityResponse.data.data.data;

      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        this.$store.dispatch('errors/setErrors', {
          general: ['Failed to load dashboard data. Please try again.']
        });
      } finally {
        this.loading = false;
      }
    },

    viewLeadDetails(lead) {
      this.$router.push(`/agents/leadranker-ai/leads/${lead.id}`);
    },

    getTemperatureColor(temperature) {
      const colors = {
        hot: 'red',
        warm: 'orange',
        cold: 'blue'
      };
      return colors[temperature] || 'grey';
    },

    getTemperatureIcon(temperature) {
      const icons = {
        hot: 'mdi-fire',
        warm: 'mdi-thermometer-plus',
        cold: 'mdi-snowflake'
      };
      return icons[temperature] || 'mdi-thermometer';
    },

    getScoreColor(score) {
      if (score >= 80) return 'red';
      if (score >= 60) return 'orange';
      if (score >= 40) return 'yellow';
      return 'grey';
    },

    getRecommendationColor(recommendation) {
      const colors = {
        pursue: 'green',
        nurture: 'orange',
        disqualify: 'red'
      };
      return colors[recommendation] || 'grey';
    },

    formatDate(date) {
      return new Date(date).toLocaleDateString() + ' ' + new Date(date).toLocaleTimeString();
    }
  }
};
</script>

<style scoped>
.leadranker-dashboard {
  min-height: calc(100vh - 64px);
}

/* Page Header */
.page-header {
  background: white;
  border-radius: 20px;
  padding: 16px 20px;
  box-shadow: 0 10px 40px rgba(102, 126, 234, 0.1);
  border: 1px solid rgba(102, 126, 234, 0.1);
  position: relative;
  overflow: hidden;
}

.page-header::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.page-title {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  font-weight: 700;
  font-size: 28px;
  margin: 0;
  display: flex;
  align-items: center;
  flex-direction: column;
  align-items: flex-start;
}

.page-inline-subtitle {
  font-size: 14px;
  color: #64748b;
  font-weight: 400;
  margin-top: 4px;
  -webkit-text-fill-color: #64748b;
}

.vibrant-title-icon {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}

/* Stats Cards */
.stats-card {
  background: white;
  border-radius: 16px;
  border: 1px solid rgba(102, 126, 234, 0.1);
  transition: all 0.3s ease;
}

.stats-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 20px 40px rgba(102, 126, 234, 0.15);
}

.stats-content {
  display: flex;
  align-items: center;
  gap: 16px;
}

.stats-icon-wrapper {
  width: 56px;
  height: 56px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.blue--bg { background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); }
.red--bg { background: linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%); }
.orange--bg { background: linear-gradient(135deg, #ffa502 0%, #ff7675 100%); }
.green--bg { background: linear-gradient(135deg, #00b894 0%, #00cec9 100%); }

.stats-text {
  flex: 1;
}

.stats-value {
  font-size: 24px;
  font-weight: 700;
  color: #2d3748;
  line-height: 1;
}

.stats-label {
  font-size: 14px;
  color: #64748b;
  margin-top: 4px;
}

/* Card Titles */
.modern-card-title {
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(240, 147, 251, 0.05) 100%);
  border-bottom: 1px solid rgba(102, 126, 234, 0.1);
}

.card-title {
  font-size: 18px;
  font-weight: 600;
  color: #2d3748;
}

/* Hot Leads */
.hot-leads-list {
  background: transparent;
  padding: 0;
}

.hot-lead-item {
  border-radius: 12px;
  margin-bottom: 8px;
  transition: all 0.3s ease;
  cursor: pointer;
}

.hot-lead-item:hover {
  background: rgba(102, 126, 234, 0.05);
  transform: translateX(4px);
}

.lead-name {
  font-weight: 600;
  color: #2d3748;
}

.lead-company {
  color: #64748b;
  font-size: 14px;
}

/* Daily Summary */
.summary-section {
  border-bottom: 1px solid rgba(102, 126, 234, 0.1);
  padding-bottom: 16px;
}

.summary-section:last-child {
  border-bottom: none;
  padding-bottom: 0;
}

.summary-heading {
  font-size: 16px;
  font-weight: 600;
  color: #2d3748;
  margin-bottom: 12px;
}

.summary-stats {
  background: rgba(102, 126, 234, 0.05);
  border-radius: 8px;
  padding: 12px;
}

.stat-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.stat-row:last-child {
  margin-bottom: 0;
}

.stat-label {
  font-size: 14px;
  color: #64748b;
}

.stat-value {
  font-weight: 600;
  color: #2d3748;
}

.temperature-changes {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.action-items {
  background: transparent;
  padding: 0;
}

/* Modern Buttons */
.modern-btn {
  border-radius: 12px !important;
  text-transform: none !important;
  font-weight: 600 !important;
  transition: all 0.3s ease !important;
}

.modern-btn:hover {
  transform: translateY(-2px);
}
</style>